import React from 'react';
import throttle from 'lodash-es/throttle';
import { useRouter } from 'next/router';
import { trackClick, trackEvent } from '@kcd/event-tracker';

import type { OAISchema } from '~/types/openapi';

export const useIntersectionObserver = (
  callback: () => void,
  options?: IntersectionObserverInit,
) => {
  const ref = React.useRef(null);
  const optionsRef = React.useRef<IntersectionObserverInit | undefined>(
    options,
  );
  const callbackRef = React.useRef(callback);
  const eventTracked = React.useRef(false);

  const handleViewedEvent: IntersectionObserverCallback = React.useCallback(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          eventTracked.current = false;
          return;
        }

        if (eventTracked.current) {
          return;
        }

        callbackRef.current();
        eventTracked.current = true;
      });
    },
    [],
  );

  React.useEffect(() => {
    optionsRef.current = options;
    callbackRef.current = callback;
  });

  React.useEffect(() => {
    if (!ref.current) {
      return () => ({});
    }

    const target = ref.current;
    const observer = new IntersectionObserver(handleViewedEvent, {
      threshold: 0.85,
      ...optionsRef.current,
    });

    observer.observe(target);
    return () => {
      observer.unobserve(target);
    };
  }, [handleViewedEvent]);

  return ref;
};

export const useTrackClick = () => {
  const router = useRouter();
  return ({
    type,
    properties,
    name,
    pageName,
  }: Omit<Parameters<typeof trackClick>[0], 'pageName'> & {
    pageName?: string;
  }) =>
    trackClick({
      type,
      name,
      pageName: pageName || `캐시노트마켓${router.pathname}`,
      // eslint-disable-next-line no-restricted-syntax
      properties: { ...properties, query: router.query },
    });
};

// [TODO] 제거예정
export const useTrackProductEventProps = (
  product:
    | Partial<OAISchema['MarketSelectProductResponseV2']>
    | Partial<OAISchema['MarketProductOrderResponse']>,
  props?: Record<string, unknown>,
) => {
  const router = useRouter();

  return [
    {
      onClick: throttle(() => {
        trackEvent('clicked 캐시노트마켓 product', {
          productName: product.productName,
          product,
          pageName: router.route,
          query: router.query,
          path: router.pathname,
          ...props,
        });
      }),
    },
    {
      trackAddCart: throttle(() => {
        trackEvent('clicked 캐시노트마켓 product addCart', {
          productName: product.productName,
          product,
          pageName: router.route,
          query: router.query,
          path: router.pathname,
          ...props,
        });
      }),
    },
  ];
};

export function useTrack() {
  const track = (name: string, properties?: Record<string, unknown>) =>
    trackEvent(name, properties);

  return {
    track,
  };
}
